import React, { Component } from 'react';
import { isEmpty } from 'lodash';
import { FormattedMessage, defineMessages, injectIntl } from 'react-intl';

import ElementWithPopover from '../element-with-popover';
import PaymentSchedule from '../payments-schedule';
import CurrencyConversion from '../../containers/currency-conversion.container';
import AvatarImg from '../../../components/avatar-img';

import { format } from '../../lib/money';
import Membership from '../../lib/membership';
import Payment from '../../lib/payment';
import { formatMessage } from '../../lib/intl';

import '../../css/member-card.css';

const i18n = defineMessages({
  friendOf: {
    id: 'memberCard.dashboard.friendOf',
    defaultMessage: 'Friend of {organizerName}',
  },
  paidShareOfThePurchase: {
    id: 'memberCard.dashboard.paidShareOfThePurchase',
    defaultMessage: 'Paid share of the purchase',
  },
  committedUpToButShort: {
    id: 'memberCard.dashboard.committedUpToButShort',
    defaultMessage: 'Committed up to {allowedShare}, but new required share is {shareAmount}',
  },
  paymentFailed: {
    id: 'memberCard.dashboard.paymentFailed',
    defaultMessage: 'Payment failed, so it must be resolved.',
  },
  currentProjectedShare: {
    id: 'memberCard.dashboard.currentProjectedShare',
    defaultMessage: 'Current projected share of the purchase.',
  },
  currentProjectedShareAcrossPayments: {
    id: 'memberCard.dashboard.currentProjectedShareAcrossPayments',
    defaultMessage: 'Current projected share of this purchase across all its installment payments.',
  },
});

export class MemberCard extends Component {
  constructor(props) {
    super(props);
    this.onRemove = this.onRemove.bind(this);
    this.formatMessage = formatMessage(this.props.intl);
  }

  onRemove() {
    this.props.onRemoveMember(this.props.membership);
  }

  contributionSchedule() {
    const { contributions } = this.props.membership;

    if (!contributions) return [];

    return contributions.map(contribution => ({
      status: contribution.status,
      amount: contribution.shareAmount,
      dueDeadline: (contribution.chargedAt || contribution.dueDeadline),
    }));
  }

  get viewAsOrganizer() {
    return this.props.viewAs === 'organizer';
  }

  get hasContributions() {
    const { contributions } = this.props.membership;
    return contributions && contributions.length > 1;
  }

  shouldRenderActions() {
    if (!this.props.showActions) return false;
    const { membership } = this.props;
    const isOrganizer = Membership.isOrganizer(membership.role);
    return this.viewAsOrganizer && !isOrganizer;
  }

  renderActions() {
    if (!this.shouldRenderActions()) return null;

    return (
      <div className="card-actions">
        <div className="dropdown">
          <a
            href="#actions"
            className="actions btn dropdown-toggle"
            data-toggle="dropdown"
            aria-haspopup="true"
            aria-expanded="false"
          >
            <i className="fa fa-ellipsis-h" />
          </a>
          <div className="dropdown-menu" aria-labelledby="dropdownMenuLink">
            <a
              className="dropdown-item"
              href="#remove-from-group"
              onClick={this.onRemove}
            >
              <FormattedMessage
                id="memberCard.dashboard.remove"
                defaultMessage="Remove"
              />
            </a>
          </div>
        </div>
      </div>
    );
  }

  renderAmountValue() {
    if (!this.viewAsOrganizer) return null;

    const { membership, payments = [] } = this.props;
    const { contributions = [], status, short, failed } = membership;
    const isCommitted = Membership.isCommitted(status);
    const paidContributions = Membership.getPaidContributions(contributions) || [];
    const allPaymentsPaid = payments.every(p => Payment.isPaid(p.status));
    let title;

    if (allPaymentsPaid && paidContributions.length > 0) {
      title = this.formatMessage(i18n.paidShareOfThePurchase);
      return this.renderAmount(membership.collectedShareAmount, title);
    }

    if (isCommitted && short && !failed) {
      title = this.formatMessage(i18n.committedUpToButShort, {
        allowedShare: format(membership.allowedShareAmount),
        shareAmount: format(membership.shareAmount),
      });
      return this.renderAmountWithIssue(membership.allowedShareAmount, title);
    }

    if (isCommitted) {
      if (failed) {
        title = this.formatMessage(i18n.paymentFailed);
        return this.renderAmountWithIssue(membership.shareAmount, title);
      }

      if (payments.length > 1) {
        title = this.formatMessage(i18n.currentProjectedShareAcrossPayments);
      } else {
        title = this.formatMessage(i18n.currentProjectedShare);
      }
      return this.renderAmount(membership.shareAmount, title);
    }
  }

  renderAmount(amount, title) {
    const formattedAmount = format(amount);

    return (
      <span className="cool-grey" title={title}>
        <i className="fa fa-fw fa-check mr-1" />
        {formattedAmount}
        {this.renderPaymentSchedule()}
        <CurrencyConversion amount={amount} />
      </span>
    );
  }

  renderAmountWithIssue(amount, title) {
    const formattedAmount = format(amount);

    return (
      <span className="coral-error" title={title}>
        <i className="fa fa-exclamation-circle mr-1" />
        {formattedAmount}
      </span>
    );
  }

  renderPaymentSchedule() {
    if (!this.hasContributions) return null;

    const payments = this.contributionSchedule();
    const content = (
      <PaymentSchedule
        payments={payments}
        totalAmount={this.props.membership.amount}
        consumerFee={this.props.membership.fees.consumerFee}
        refundableAmount={this.props.membership.refundableShareAmount}
        forMember
      />
    );

    return (
      <ElementWithPopover
        name="member-schedule"
        className="member-schedule-popover d-inline-block"
        content={content}
      >
        <i className="fa fa-fw fa-clock-o cool-grey" />
      </ElementWithPopover>
    );
  }

  renderUserName() {
    const { membership: { user }, organizerName } = this.props;

    let displayName = this.formatMessage(i18n.friendOf, { organizerName });

    if (!isEmpty(user.fullName)) {
      displayName = user.fullName;
    } else if (this.viewAsOrganizer && !isEmpty(user.email)) {
      displayName = user.email;
    }

    return (
      <span className="d-block member-name" title={displayName}>
        {displayName}
      </span>
    );
  }

  render() {
    const { user } = this.props.membership;

    return (
      <div className="user-card card">
        {this.renderActions()}
        <div className="row">
          <div className="d-flex align-items-center">
            <div className="member-avatar mr-3">
              <AvatarImg user={user} small />
            </div>
            <div className="info">
              {this.renderUserName()}
              {this.renderAmountValue()}
            </div>
          </div>
        </div>
      </div>
    );
  }
}

MemberCard.defaultProps = {
  membership: null,
  organizerName: 'Organizer',
  viewAs: 'invitee', // or 'organizer'
  showActions: true,
  onRemoveMember: () => { },
};

export default injectIntl(MemberCard);



// WEBPACK FOOTER //
// ./src/desktop/components/purchase-dashboard/member-card.js